import React from "react";
import { useDelete } from "../../api/useCall";
import type { Source } from "../../api/useSources";
import t from "../../i18n";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import InputV2 from "@ui/Input/InputV2";
import ModalForm from "@ui/Modal/Form";
import { useMutateBody } from "../../api/useQuery";

interface P {
  source: Source;
  onRequestClose: () => void;
}

const DeleteModalForm = ({ source, onRequestClose }: P) => {
  const { addToast } = useToasts();
  const sourceId = useInput("");
  const mutateSources = useMutateBody("/sources");

  const deleteSource = useDelete("/sources/:sourceId");

  const handleSubmit = React.useCallback(() => {
    const removeInfo = addToast("info", t("sources.delete.toast.info", { name: source.id }));
    const rollback = mutateSources(sources => sources.filter(s => s.id !== source.id));

    deleteSource({ routeParams: { sourceId: encodeURIComponent(source.id) } })
      .then(() => {
        removeInfo();
        addToast("success", t("sources.delete.toast.success", { name: source.id }));
      })
      .catch(error => {
        removeInfo();
        rollback();
        throw error;
      });

    return true;
  }, [addToast, deleteSource, mutateSources, source]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={sourceId.value === source.id ? handleSubmit : false}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      <div>{t("sources.delete.warning", { name: source.id })}</div>
      <InputV2
        label={t("sources.delete.label")}
        required
        placeholder={t("sources.delete.placeholder")}
        value={sourceId.value}
        onChange={e => sourceId.onChange(e)}
      />
    </ModalForm>
  );
};

export default DeleteModalForm;
